/**
 * SmartImage — <picture> wrapper that serves AVIF → WebP → original.
 * Zero visual change; browsers pick the best format they support.
 *
 * Usage: <SmartImage src="/assets/foo.png" alt="..." className="..." />
 *
 * Notes:
 *   • Pass `eager` for above-the-fold hero images (e.g. Hero bull) —
 *     defaults to lazy loading + async decoding.
 *   • Uses `fetchpriority="high"` when `eager` is true so the browser
 *     downloads it before other resources.
 *   • Only rewrites .png / .jpg / .jpeg → .avif / .webp. External URLs,
 *     .svg, .gif, etc. pass through as a plain <img>.
 */
const swapExt = (src, ext) => src.replace(/\.(png|jpe?g)$/i, `.${ext}`);

export const SmartImage = ({
  src,
  alt = "",
  eager = false,
  className,
  style,
  onClick,
  onLoad,
  onError,
  ...rest
}) => {
  const isLocalRaster =
    typeof src === "string" &&
    !/^https?:\/\//i.test(src) &&
    /\.(png|jpe?g)$/i.test(src);

  const loading = eager ? "eager" : "lazy";
  const fetchPriority = eager ? "high" : "auto";
  const decoding = eager ? "sync" : "async";

  const imgEl = (
    <img
      src={src}
      alt={alt}
      loading={loading}
      decoding={decoding}
      // eslint-disable-next-line react/no-unknown-property
      fetchpriority={fetchPriority}
      className={className}
      style={style}
      onClick={onClick}
      onLoad={onLoad}
      onError={onError}
      {...rest}
    />
  );

  if (!isLocalRaster) return imgEl;

  return (
    <picture>
      <source srcSet={swapExt(src, "avif")} type="image/avif" />
      <source srcSet={swapExt(src, "webp")} type="image/webp" />
      {imgEl}
    </picture>
  );
};

export default SmartImage;
